// A chart at full size. Chat bubbles are narrow — 300px in a docked widget —
// so a 24h line with two series is legible but not readable: you can see the
// shape, not the hour something happened. Expanding gives the same chart the
// whole viewport, with more x labels because there is now room for them.
//
// Dismissal mirrors Outlook's dialog: ×, Escape, or a press outside the card.
// useOutsidePress walks the composed path, so one mechanism covers the widget's
// shadow DOM and the host page alike. No focus trap, hence no aria-modal claim.

import { useEffect, useRef, useState } from 'react'
import type { Chart as ChartJSInstance } from 'chart.js'

import { ChartActions } from './ChartActions'
import { NwChart } from './NwChart'
import { expandedSpec, type ChartSpec } from './chartSpec'

export interface ChartDialogProps {
  spec: ChartSpec
  onClose: () => void
}

/** Fill the viewport, minus the dialog's own chrome and padding. */
function dialogChartHeight(): number {
  const viewport = typeof window === 'undefined' ? 720 : window.innerHeight
  return Math.max(220, Math.min(viewport - 220, 620))
}

export function ChartDialog({ spec, onClose }: ChartDialogProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [chart, setChart] = useState<{ instance: ChartJSInstance; surface: string } | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    function onKey(ev: globalThis.KeyboardEvent) {
      if (ev.key === 'Escape') onCloseRef.current()
    }
    function onPointerDown(ev: PointerEvent) {
      if (cardRef.current && !ev.composedPath().includes(cardRef.current)) onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [])

  const big = expandedSpec(spec, dialogChartHeight())

  return (
    <div className="nw-nchart-dim">
      <div ref={cardRef} className="nw-nchart-dialog" role="dialog" aria-label={spec.title || 'Chart'}>
        <div className="nw-nchart-dlg-head">
          <div className="nw-nchart-dlg-title">{spec.title || 'Chart'}</div>
          {/* Export the chart you are actually looking at — the big one. */}
          <ChartActions spec={big} chart={chart} />
          <button type="button" className="nw-nchart-dlg-close" onClick={onClose} aria-label="Close chart">✕</button>
        </div>
        <NwChart
          spec={big}
          zoomable
          onReady={(instance, surface) => setChart({ instance, surface })}
          inkVar="--nw-text"
          faintVar="--nw-text-tertiary"
          gridVar="--nw-border"
          thresholdVar="--nw-danger"
          surfaceVar="--nw-bg"
        />
        <div className="nw-nchart-more">
          {spec.notes.length > 0 && <span>{spec.notes.join(' · ')} · </span>}
          {!spec.categorical && spec.type !== 'pie' && spec.type !== 'doughnut'
            ? 'Drag across the chart to zoom · double-click to reset'
            : null}
        </div>
      </div>
    </div>
  )
}
